import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  CONSENT_JOURNAL_INVALID_CURSOR,
  type ConsentJournalScope,
} from '@aica/api-types/schemas';
import type { ConsentEvent } from '@/lib/api/domains/consent.api';
import { ApiError } from '@/lib/fetch-utils';
import { useApiClient } from '@/hooks/useApiClient';
import { useRole } from '@/contexts/RoleContext';
import { logError } from '@/lib/logger';

const PAGE_SIZE = 20;

/**
 * Where the next page starts: the server's cursor, or — only against a server
 * from before the cursor (#1397), during a deploy skew or an edge-api
 * rollback — an offset, the way the journal paged before.
 */
type PageParam = string | { offset: number } | undefined;

const isInvalidCursor = (error: unknown) =>
  error instanceof ApiError &&
  error.status === 400 &&
  (error.data as { code?: unknown } | undefined)?.code === CONSENT_JOURNAL_INVALID_CURSOR;

/**
 * One half of the consent journal — participant consents or the account's
 * agreement (#1100) — read the same way for both.
 *
 * Pages accumulate: «Load more» adds to what is shown instead of replacing it.
 * They live under the organization's own key, so switching organizations
 * starts from that organization's first page.
 *
 * Each page continues from a cursor — the last row it showed — not from an
 * offset (#1397). The journal grows at the top, newest first: with offsets a
 * row written between two pages shifted the next page by one, so it repeated
 * a row and the new one, landing on a page already read, never came. A cursor
 * names the row to continue after, so an insert leaves it in place. A row
 * written above the first page is not lost either: every page says which row
 * is newest in the journal now, and when that is not the first row shown the
 * journal says there are newer entries and offers to read it again.
 */
export function useConsentJournal(scope: ConsentJournalScope, type?: string) {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['consent-events', selectedOrgId, scope, type ?? 'all'] as const,
    [selectedOrgId, scope, type]
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam: PageParam }) =>
      apiClient.consent.listEvents({
        scope,
        type,
        limit: PAGE_SIZE,
        ...(typeof pageParam === 'object' ? { offset: pageParam.offset } : {}),
        ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as PageParam,
    getNextPageParam: (last, pages): PageParam => {
      // `nextCursor`, not `total`: the server counts in a query of its own, at
      // another moment than the page. `null` is the end of the journal.
      if (last.nextCursor !== undefined) return last.nextCursor ?? undefined;
      // No field at all: a server from before the cursor ignores `cursor` and
      // would serve the first page again. Page by offset, as before.
      const loaded = pages.reduce((sum, page) => sum + page.events.length, 0);
      return last.events.length > 0 && (last.total ?? 0) > loaded
        ? { offset: loaded }
        : undefined;
    },
    enabled: !!selectedOrgId,
  });

  const pages = query.data?.pages;
  // Each row once. With a cursor a row cannot come twice; the offset path
  // above (an old server) can, when a row is written between two pages.
  const { events, repeated } = useMemo(() => {
    const seen = new Set<string>();
    const unique: ConsentEvent[] = [];
    let repeat = false;
    for (const event of pages?.flatMap((page) => page.events) ?? []) {
      if (seen.has(event.id)) {
        repeat = true;
        continue;
      }
      seen.add(event.id);
      unique.push(event);
    }
    return { events: unique, repeated: repeat };
  }, [pages]);

  // An event was written above rows already shown, and the open journal will
  // not see it without reading again from the top. With a cursor the server
  // says how many rows now sort at or above it; this journal showed exactly
  // as many before that page — more means an insert anywhere above, a late
  // row at the top or a backdated one in between. Against an old server (no
  // cursor) the offsets shift instead: a repeated row or a changed total.
  // Said, not acted on: the reader chooses to read again.
  const newerAvailable = useMemo(() => {
    if (!pages) return false;
    let shownBefore = 0;
    for (const page of pages) {
      if (shownBefore > 0 && page.rowsUpToCursor !== undefined) {
        if (page.rowsUpToCursor !== shownBefore) return true;
      }
      shownBefore += page.events.length;
    }
    const totals = pages.map((page) => page.total).filter((total) => total !== undefined);
    return repeated || totals.some((total) => total !== totals[0]);
  }, [pages, repeated]);

  // The server stopped speaking cursors mid-journal (an edge-api rollback
  // between two pages): it ignored the cursor and served its first page
  // again, without `nextCursor`. Mixed pages cannot be continued — read the
  // journal again from the top, where it pages by offset (Codex, #1455).
  const protocolChanged =
    !!pages &&
    pages.length > 1 &&
    pages[0]!.nextCursor !== undefined &&
    pages[pages.length - 1]!.nextCursor === undefined;
  useEffect(() => {
    if (protocolChanged) void queryClient.resetQueries({ queryKey, exact: true });
  }, [protocolChanged, queryClient, queryKey]);

  // A cursor the server no longer recognizes (its row is gone, or the query it
  // belongs to changed) fails the same way on every retry: start the journal
  // over from its first page instead of offering a retry that cannot succeed.
  const cursorRejected = query.isFetchNextPageError && isInvalidCursor(query.error);
  useEffect(() => {
    if (cursorRejected) void queryClient.resetQueries({ queryKey, exact: true });
  }, [cursorRejected, queryClient, queryKey]);

  // A refetch over pages already shown (after a withdrawal, say) that failed
  // keeps those pages: react-query retains `data`, so neither «nothing to
  // show» nor «a later page failed» is true, and without its own flag the
  // journal went on showing the old rows as if they were current.
  const refreshFailed = query.isRefetchError;
  useEffect(() => {
    if (refreshFailed) {
      logError(
        'consent.journal_refresh_failed',
        { component: 'useConsentJournal', scope },
        query.error instanceof Error ? query.error : undefined
      );
    }
  }, [refreshFailed, query.error, scope]);

  return {
    events,
    // Pending, not loading: a query waiting for the organization is not
    // «nothing recorded» (react-query v5 — `isLoading` is false while disabled).
    isPending: query.isPending,
    /** The first page failed: nothing to show. */
    loadFailed: query.isError && query.data === undefined,
    /** A later page failed: what is shown stays, with a note under it. */
    loadMoreFailed: query.isFetchNextPageError && !cursorRejected,
    /** Re-reading the shown pages failed: they stay, marked as possibly stale. */
    refreshFailed,
    /** An event was written above the rows shown: the journal says so. */
    newerAvailable,
    refresh: () => void query.refetch(),
    error: query.error,
    hasMore: query.hasNextPage,
    /**
     * «Load more» waits for any read in flight, not only its own. A page
     * fetched over a re-read would cancel it (react-query's default), and the
     * re-read is what brings in the withdrawal just made: the journal would go
     * on without it. `cancelRefetch: false` holds that for a click that slips
     * through.
     */
    loadMoreBlocked: query.isFetching,
    loadMore: () => void query.fetchNextPage({ cancelRefetch: false }),
  };
}
